import React, { useState, useEffect } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { tokenManager } from "../../../utils/tokenManager";

const regions = [
  'Brong Ahafo Central',
  'Brong Ahafo North',
  'Brong Ahafo South',
  'Brazil'
];

const provinceCities = [
  'Techiman',
  'Nkoranza',
  'Kintampo',
  'Wenchi',
  'Nsawkaw',
  'Banda',
  'Nkrankwanta',
  'Danyame',
  'Berekum',
  'Dormaa',
  'Drobo',
  'Sampa',
  'Nalerigu',
  'Yamatwa',
  'Kofibadukrom',
  'Kwameseikrom',
  'Kwameprakrom',
  'Brazil'
];

const countries = [
  'Ghana',
  'Brazil'
];

const AddRawRCN = () => {
  const initialFormState = {
    date: "",
    numberOfTrucks: "",
    numberOfBags: "",
    rcn_type: "",
    statusApproval: "",
    agent: "",
    conventionalCode: "",
    truckNumber: "",
    reanalysis: "",
    region: "",
    provinceOrCity: "",
    country: "",
    code: "",
    countPerKg: "",
    moisture: "",
    outturn: "",
    dustAfterDrying: "",
    dustAfterCalibration: "",
    grossWeight: "",
    netWeight: "",
    size15: "",
    size17: "",
    size19: "",
    size21: "",
    size23: "",
    size25: "",
    size28: "",
    totalPercentage: "",
    impuritiesPercentage: "",
    boredPercentage: "",
    voidPercentage: "",
    oilPercentage: "",
    rottenPercentage: "",
    totalImpuritiesPercentage: "",
    impuritiesG: "",
    boredG: "",
    voidG: "",
    oilG: "",
    rottenG: "",
    immatureG: "",
    spottedG: "",
    immaturePercentage: "",
    spottedPercentage: "",
    totalSpPlusImPercentage: "",
    totalDefectiveRatePercentage: "",
    veryBad: "",
    bad: "",
    regular: "",
    good: "",
    veryGood: "",
    expectedKernelOutturn: "",
    origin: "",
    qualityRating: "",
    goodMeasuredInGrams: "",
    goodPercentage: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  // Calculate quality rating based on TDR percentage
  const calculateQualityRating = (tdrPercentage) => {
    const tdr = parseFloat(tdrPercentage) || 0;
    let qualityRating = '';
    let ratingColor = '';
    
    if (tdr >= 0 && tdr <= 6.9) {
      qualityRating = 'Very Good';
      ratingColor = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    } else if (tdr >= 7 && tdr <= 10.9) {
      qualityRating = 'Good';
      ratingColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    } else if (tdr >= 11 && tdr <= 15.9) {
      qualityRating = 'Regular';
      ratingColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    } else if (tdr >= 16 && tdr <= 20.9) {
      qualityRating = 'Bad';
      ratingColor = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    } else if (tdr >= 21) {
      qualityRating = 'Very Bad';
      ratingColor = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    }
    
    return { qualityRating, ratingColor };
  };

  // Map UI RCN type labels to API expected values
  const mapRcnType = (type) => {
    const normalized = (type || '').toLowerCase();
    if (normalized.includes('organic')) return 'organic';
    if (normalized.includes('fair')) return 'fair_trade';
    if (normalized.includes('conventional')) return 'conventional';
    return normalized || 'conventional';
  };

  // Convert quality label to grade expected by API
  const mapQualityToGrade = (qualityLabel) => {
    switch ((qualityLabel || '').toLowerCase()) {
      case 'very good':
        return 'A';
      case 'good':
        return 'B';
      case 'regular':
        return 'C';
      case 'bad':
        return 'D';
      case 'very bad':
        return 'E';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { id, name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [type === 'radio' ? name : id]: value
    }));
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Transform the data to match the exact API parameter format
      // Derive quality from auto-calculated TDR to ensure consistency with UI
      const autoQuality = calculateQualityRating(formData.totalDefectiveRatePercentage).qualityRating;
      const transformedData = {
        date: formData.date,
        number_of_trucks: parseInt(formData.numberOfTrucks),
        number_of_bags: parseInt(formData.numberOfBags),
        rcn_type: mapRcnType(formData.rcn_type),
        status_approval: formData.statusApproval || 'pending',
        agent: formData.agent,
        conventional_code: formData.conventionalCode,
        truck_number: formData.truckNumber,
        reanalysis: (formData.reanalysis || 'no').toLowerCase(),
        region: formData.region,
        province_or_city: formData.provinceOrCity,
        country: formData.country,
        code: formData.code,
        count_per_kg: parseFloat(formData.countPerKg),
        moisture: parseFloat(formData.moisture),
        outturn: parseFloat(formData.outturn),
        dust_after_drying: parseFloat(formData.dustAfterDrying),
        dust_after_calibration: parseFloat(formData.dustAfterCalibration),
        gross_weight: parseFloat(formData.grossWeight),
        net_weight: parseFloat(formData.netWeight),
        size_15: parseFloat(formData.size15),
        size_17: parseFloat(formData.size17),
        size_19: parseFloat(formData.size19),
        size_21: parseFloat(formData.size21),
        size_23: parseFloat(formData.size23),
        size_25: parseFloat(formData.size25),
        size_28: parseFloat(formData.size28),
        total_size: parseFloat(formData.totalPercentage),
        impurities: parseFloat(formData.impuritiesPercentage),
        bored: parseFloat(formData.boredPercentage),
        void: parseFloat(formData.voidPercentage),
        oil: parseFloat(formData.oilPercentage),
        rotten: parseFloat(formData.rottenPercentage),
        total_impurities: parseFloat(formData.totalImpuritiesPercentage),
        immature_percentage: parseFloat(formData.immaturePercentage),
        spotted_percentage: parseFloat(formData.spottedPercentage),
        total_sp_plus_im_percentage: parseFloat(formData.totalSpPlusImPercentage),
        total_defective_rate_percentage: parseFloat(formData.totalDefectiveRatePercentage),
        very_bad: parseFloat(formData.veryBad),
        bad: parseFloat(formData.bad),
        regular: parseFloat(formData.regular),
        good: parseFloat(formData.good),
        very_good: parseFloat(formData.veryGood),
        expected_kernel_outturn: parseFloat(formData.expectedKernelOutturn),
        origin: formData.origin,
        quality_rcn: autoQuality,
        good_measured_in_grams: parseFloat(formData.goodMeasuredInGrams),
        good_percentage: parseFloat(formData.goodPercentage),
      };

      // Log the transformed data
      console.log('Submitting transformed data:', transformedData);

      const response = await fetch("https://usibras-web-backend.onrender.com/api/new-raw-rcn", {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Server error response:', data);
        throw new Error(data.message || data.error || 'Something went wrong');
      }
      
      showAlert(`Successfully analyzed! ${data.message}`, 'success');
      setFormData(initialFormState);
    } catch (error) {
      console.error('Error details:', error);
      showAlert(error.message || "An error occurred", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Add CSS styles for hiding number input spinners
  const styles = `
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
  `;

  // Prevent scroll from changing number input values
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div className="mt-5 h-full">
      <style>{styles}</style>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-navy-700 font-medium">Processing...</p>
          </div>
        </div>
      )}

      {alert.show && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
          alert.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-50`}>
          {alert.message}
        </div>
      )}

      <Card extra={"w-full p-4"}>
        <div className="mb-8 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Add Raw Cashew Nut
          </h4>
          <p className="mt-2 text-base text-gray-600">
            Please fill in all the required information
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Basic Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Date*"
                id="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
              <InputField
                label="Order of Trucks*"
                id="numberOfTrucks"
                type="number"
                placeholder="Enter order of trucks"
                value={formData.numberOfTrucks}
                onChange={handleChange}
              />
              <InputField
                label="Number of Bags*"
                id="numberOfBags"
                type="number"
                placeholder="Enter number of bags"
                value={formData.numberOfBags}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      numberOfBags: value
                    };
                    // Calculate net weight: Gross Weight - Number of Bags (1 bag = 1 kg)
                    const grossWeight = parseFloat(newFormData.grossWeight || 0);
                    const numberOfBags = parseFloat(value || 0);
                    const netWeight = (grossWeight - numberOfBags).toFixed(2);
                    
                    return {
                      ...newFormData,
                      netWeight: netWeight
                    };
                  });
                }}
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Select RCN Type*
                </label>
                <select
                  id="rcn_type"
                  value={formData.rcn_type}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">Select RCN type</option>
                  <option value="Fair Trade">Fair Trade</option>
                  <option value="Conventional RCN">Conventional RCN</option>
                  <option value="Organic RCN">Organic RCN</option>
                </select>
              </div>
              {/* <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Status Approval*
                </label>
                <select
                  id="statusApproval"
                  value={formData.statusApproval}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  required
                >
                  <option value="">Select status</option>
                  <option value="Approve">Approve</option>
                  <option value="Reject">Reject</option>
                </select>
              </div> */}
            </div>
          </div>

          {/* Status and Agent Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Status and Agent Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Agent/Association*"
                id="agent"
                type="text"
                placeholder="Enter agent"
                value={formData.agent}
                onChange={handleChange}
              />
              {formData.rcn_type === "Organic RCN" && (
                <InputField
                  label="Organic Code"
                  id="conventionalCode"
                  type="text"
                  placeholder="Enter organic code"
                  value={formData.conventionalCode}
                  onChange={handleChange}
                />
              )}
              <InputField
                label="Truck Number*"
                id="truckNumber"
                type="text"
                placeholder="Enter truck number"
                value={formData.truckNumber}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Location Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Location Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Reanalysis*
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="reanalysis"
                      value="Yes"
                      checked={formData.reanalysis === "Yes"}
                      onChange={handleChange}
                      className="mr-2"
                      required
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="reanalysis"
                      value="No"
                      checked={formData.reanalysis === "No"}
                      onChange={handleChange}
                      className="mr-2"
                      required
                    />
                    No
                  </label>
                </div>
              </div> */}
              {/* <InputField
                label="Origin*"
                id="origin"
                type="text"
                placeholder="Enter origin"
                value={formData.origin}
                onChange={handleChange}
                required
              /> */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Region*
                </label>
                <select
                  id="region"
                  value={formData.region}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">Select region</option>
                  {regions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Province or City*
                </label>
                <select
                  id="provinceOrCity"
                  value={formData.provinceOrCity}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">Select province or city</option>
                  {provinceCities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Country*
                </label>
                <select
                  id="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">Select country</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <InputField
                label="Code*"
                id="code"
                type="text"
                placeholder="Enter code"
                value={formData.code}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Weight Inventory */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Weight Inventory
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Dust After Drying"
                id="dustAfterDrying"
                type="number"
                step="0.01"
                placeholder="Enter dust after drying"
                value={formData.dustAfterDrying}
                onChange={handleChange}
              />
              <InputField
                label="Dust After Calibration"
                id="dustAfterCalibration"
                type="number"
                step="0.01"
                placeholder="Enter dust after calibration"
                value={formData.dustAfterCalibration}
                onChange={handleChange}
              />
              <InputField
                label="Gross Weight (kg)*"
                id="grossWeight"
                type="number"
                step="0.01"
                placeholder="Enter gross weight"
                value={formData.grossWeight}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      grossWeight: value
                    };
                    // Calculate net weight: Gross Weight - Number of Bags (1 bag = 1 kg)
                    const grossWeight = parseFloat(value || 0);
                    const numberOfBags = parseFloat(newFormData.numberOfBags || 0);
                    const netWeight = (grossWeight - numberOfBags).toFixed(2);
                    
                    return {
                      ...newFormData,
                      netWeight: netWeight
                    };
                  });
                }}
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Net Weight* (Auto-calculated)
                </label>
                <input
                  id="netWeight"
                  type="number"
                  step="0.01"
                  value={formData.netWeight || '0.00'}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Calculated as: Gross Weight - Number of Bags (1 bag = 1 kg)
                </p>
              </div>
            </div>
          </div>

          {/* Size Distribution */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Size Distribution in Percentage(%)
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Size 15*"
                id="size15"
                type="number"
                step="0.01"
                placeholder="Enter size 15"
                value={formData.size15}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size15: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
              />
              <InputField
                label="Size 17*"
                id="size17"
                type="number"
                step="0.01"
                placeholder="Enter size 17"
                value={formData.size17}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size17: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
              />
              <InputField
                label="Size 19*"
                id="size19"
                type="number"
                step="0.01"
                placeholder="Enter size 19"
                value={formData.size19}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size19: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
              />
              <InputField
                label="Size 21*"
                id="size21"
                type="number"
                step="0.01"
                placeholder="Enter size 21"
                value={formData.size21}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size21: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
              />
              <InputField
                label="Size 23*"
                id="size23"
                type="number"
                step="0.01"
                placeholder="Enter size 23"
                value={formData.size23}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size23: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
              />
              <InputField
                label="Size 25*"
                id="size25"
                type="number"
                step="0.01"
                placeholder="Enter size 25"
                value={formData.size25}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size25: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
              />
              <InputField
                label="Size 28*"
                id="size28"
                type="number"
                step="0.01"
                placeholder="Enter size 28"
                value={formData.size28}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size28: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total %*
                </label>
                <input
                  id="totalPercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalPercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Quality Measurements */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Quality Measurements
            </h5>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-semibold">Sample Size: 1000g</span> - Enter raw gram values, percentages will be auto-calculated.
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Count/KG*"
                id="countPerKg"
                type="number"
                step="0.01"
                placeholder="Enter count per kg"
                value={formData.countPerKg}
                onChange={handleChange}
              />
              <InputField
                label="Moisture %*"
                id="moisture"
                type="number"
                step="0.01"
                placeholder="Enter moisture percentage"
                value={formData.moisture}
                onChange={handleChange}
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Outturn (LBS)* <span className="text-gray-500 text-xs">(Auto-calculated)</span>
                </label>
                <input
                  id="outturn"
                  type="number"
                  step="0.01"
                  value={formData.outturn || '0.00'}
                  readOnly
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  placeholder="Auto-calculated"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Formula: (Good + (Spotted + Immature) / 2) × 0.176
                </p>
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Good Measured (g)*
                </label>
                <InputField
                  id="goodMeasuredInGrams"
                  type="number"
                  step="0.01"
                  placeholder="Enter good measured in grams (Sample: 1000g)"
                  value={formData.goodMeasuredInGrams || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => {
                      const newFormData = {
                        ...prev,
                        goodMeasuredInGrams: value
                      };
                      // Convert grams to percentage based on 1000g sample
                      const goodGrams = parseFloat(value || 0);
                      const goodPercentage = ((goodGrams / 1000) * 100).toFixed(2);
                      newFormData.goodPercentage = goodPercentage;
                      
                      // Calculate Outturn (LBS) using formula: (Good + (Spotted + Immature) / 2) * 0.176
                      const spottedGrams = parseFloat(newFormData.spottedG || 0);
                      const immatureGrams = parseFloat(newFormData.immatureG || 0);
                      const outturnLbs = (goodGrams + (spottedGrams + immatureGrams) / 2) * 0.176;
                      newFormData.outturn = outturnLbs > 0 ? outturnLbs.toFixed(2) : '0.00';
                      
                      // Also calculate expected kernel outturn if outturn exists
                      if (newFormData.outturn && newFormData.outturn !== '0.00') {
                        const step1 = parseFloat(newFormData.outturn) / 2.2046;
                        const step2 = step1 * 1.25;
                        newFormData.expectedKernelOutturn = ((step2 / 100) * 100).toFixed(2);
                      }
                      
                      return newFormData;
                    });
                  }}
                />
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Percentage: {formData.goodPercentage || '0.00'}%
                </p>
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Impurities (g)*
                </label>
                <InputField
                  id="impuritiesG"
                  type="number"
                  step="0.01"
                  placeholder="Enter impurities in grams (Sample: 1000g)"
                  value={formData.impuritiesG || ''}
                  onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      impuritiesG: value
                    };
                    // Convert grams to percentage based on 1000g sample
                    const impuritiesGrams = parseFloat(value || 0);
                    const impuritiesPercentage = ((impuritiesGrams / 1000) * 100).toFixed(2);
                    newFormData.impuritiesPercentage = impuritiesPercentage;
                    
                    // Calculate total impurities percentage
                    const boredPercentage = parseFloat(newFormData.boredPercentage || 0);
                    const voidPercentage = parseFloat(newFormData.voidPercentage || 0);
                    const oilPercentage = parseFloat(newFormData.oilPercentage || 0);
                    const rottenPercentage = parseFloat(newFormData.rottenPercentage || 0);
                    const totalImpurities = (
                      parseFloat(impuritiesPercentage) +
                      boredPercentage +
                      voidPercentage +
                      oilPercentage +
                      rottenPercentage
                    ).toFixed(2);
                    
                    // Recalculate total SP+IM percentage
                    const totalSpPlusIm = (
                      parseFloat(newFormData.immaturePercentage || 0) +
                      parseFloat(newFormData.spottedPercentage || 0)
                    ).toFixed(2);
                    
                    // Recalculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(totalImpurities) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate quality rating based on TDR
                    const qualityData = calculateQualityRating(totalDefectiveRate);
                    
                    // Calculate Outturn (LBS)
                    const good = parseFloat(newFormData.goodMeasuredInGrams || 0);
                    const spottedGrams = parseFloat(newFormData.spottedG || 0);
                    const immatureGrams = parseFloat(newFormData.immatureG || 0);
                    const outturnLbs = (good + (spottedGrams + immatureGrams) / 2) * 0.176;
                    newFormData.outturn = outturnLbs > 0 ? outturnLbs.toFixed(2) : '0.00';
                    
                    // Calculate expected kernel outturn if outturn exists
                    if (newFormData.outturn && newFormData.outturn !== '0.00') {
                      const step1 = parseFloat(newFormData.outturn) / 2.2046;
                      const step2 = step1 * 1.25;
                      newFormData.expectedKernelOutturn = ((step2 / 100) * 100).toFixed(2);
                    }
                    
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      qualityRating: qualityData.qualityRating
                    };
                  });
                }}
              />
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Percentage: {formData.impuritiesPercentage || '0.00'}%
              </p>
            </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Bored (g)*
                </label>
                <InputField
                  id="boredG"
                  type="number"
                step="0.01"
                placeholder="Enter bored in grams (Sample: 1000g)"
                value={formData.boredG || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      boredG: value
                    };
                    // Convert grams to percentage based on 1000g sample
                    const boredGrams = parseFloat(value || 0);
                    const boredPercentage = ((boredGrams / 1000) * 100).toFixed(2);
                    newFormData.boredPercentage = boredPercentage;
                    
                    // Calculate total impurities percentage
                    const impuritiesPercentage = parseFloat(newFormData.impuritiesPercentage || 0);
                    const voidPercentage = parseFloat(newFormData.voidPercentage || 0);
                    const oilPercentage = parseFloat(newFormData.oilPercentage || 0);
                    const rottenPercentage = parseFloat(newFormData.rottenPercentage || 0);
                    const totalImpurities = (
                      impuritiesPercentage +
                      parseFloat(boredPercentage) +
                      voidPercentage +
                      oilPercentage +
                      rottenPercentage
                    ).toFixed(2);
                    
                    // Recalculate total SP+IM percentage
                    const totalSpPlusIm = (
                      parseFloat(newFormData.immaturePercentage || 0) +
                      parseFloat(newFormData.spottedPercentage || 0)
                    ).toFixed(2);
                    
                    // Recalculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(totalImpurities) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate quality rating based on TDR
                    const qualityData = calculateQualityRating(totalDefectiveRate);
                    
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      qualityRating: qualityData.qualityRating
                    };
                  });
                }}
              />
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Percentage: {formData.boredPercentage || '0.00'}%
              </p>
            </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Void (g)*
                </label>
                <InputField
                  id="voidG"
                type="number"
                step="0.01"
                placeholder="Enter void in grams (Sample: 1000g)"
                value={formData.voidG || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      voidG: value
                    };
                    // Convert grams to percentage based on 1000g sample
                    const voidGrams = parseFloat(value || 0);
                    const voidPercentage = ((voidGrams / 1000) * 100).toFixed(2);
                    newFormData.voidPercentage = voidPercentage;
                    
                    // Calculate total impurities percentage
                    const impuritiesPercentage = parseFloat(newFormData.impuritiesPercentage || 0);
                    const boredPercentage = parseFloat(newFormData.boredPercentage || 0);
                    const oilPercentage = parseFloat(newFormData.oilPercentage || 0);
                    const rottenPercentage = parseFloat(newFormData.rottenPercentage || 0);
                    const totalImpurities = (
                      impuritiesPercentage +
                      boredPercentage +
                      parseFloat(voidPercentage) +
                      oilPercentage +
                      rottenPercentage
                    ).toFixed(2);
                    
                    // Recalculate total SP+IM percentage
                    const totalSpPlusIm = (
                      parseFloat(newFormData.immaturePercentage || 0) +
                      parseFloat(newFormData.spottedPercentage || 0)
                    ).toFixed(2);
                    
                    // Recalculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(totalImpurities) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate quality rating based on TDR
                    const qualityData = calculateQualityRating(totalDefectiveRate);
                    
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      qualityRating: qualityData.qualityRating
                    };
                  });
                }}
              />
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Percentage: {formData.voidPercentage || '0.00'}%
              </p>
            </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Oil (g)*
                </label>
                <InputField
                  id="oilG"
                type="number"
                step="0.01"
                placeholder="Enter oil in grams (Sample: 1000g)"
                value={formData.oilG || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      oilG: value
                    };
                    // Convert grams to percentage based on 1000g sample
                    const oilGrams = parseFloat(value || 0);
                    const oilPercentage = ((oilGrams / 1000) * 100).toFixed(2);
                    newFormData.oilPercentage = oilPercentage;
                    
                    // Calculate total impurities percentage
                    const impuritiesPercentage = parseFloat(newFormData.impuritiesPercentage || 0);
                    const boredPercentage = parseFloat(newFormData.boredPercentage || 0);
                    const voidPercentage = parseFloat(newFormData.voidPercentage || 0);
                    const rottenPercentage = parseFloat(newFormData.rottenPercentage || 0);
                    const totalImpurities = (
                      impuritiesPercentage +
                      boredPercentage +
                      voidPercentage +
                      parseFloat(oilPercentage) +
                      rottenPercentage
                    ).toFixed(2);
                    
                    // Recalculate total SP+IM percentage
                    const totalSpPlusIm = (
                      parseFloat(newFormData.immaturePercentage || 0) +
                      parseFloat(newFormData.spottedPercentage || 0)
                    ).toFixed(2);
                    
                    // Recalculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(totalImpurities) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate quality rating based on TDR
                    const qualityData = calculateQualityRating(totalDefectiveRate);
                    
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      qualityRating: qualityData.qualityRating
                    };
                  });
                }}
              />
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Percentage: {formData.oilPercentage || '0.00'}%
              </p>
            </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Rotten (g)*
                </label>
                <InputField
                  id="rottenG"
                type="number"
                step="0.01"
                placeholder="Enter rotten in grams (Sample: 1000g)"
                value={formData.rottenG || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      rottenG: value
                    };
                    // Convert grams to percentage based on 1000g sample
                    const rottenGrams = parseFloat(value || 0);
                    const rottenPercentage = ((rottenGrams / 1000) * 100).toFixed(2);
                    newFormData.rottenPercentage = rottenPercentage;
                    
                    // Calculate total impurities percentage
                    const impuritiesPercentage = parseFloat(newFormData.impuritiesPercentage || 0);
                    const boredPercentage = parseFloat(newFormData.boredPercentage || 0);
                    const voidPercentage = parseFloat(newFormData.voidPercentage || 0);
                    const oilPercentage = parseFloat(newFormData.oilPercentage || 0);
                    const totalImpurities = (
                      impuritiesPercentage +
                      boredPercentage +
                      voidPercentage +
                      oilPercentage +
                      parseFloat(rottenPercentage)
                    ).toFixed(2);
                    
                    // Recalculate total SP+IM percentage
                    const totalSpPlusIm = (
                      parseFloat(newFormData.immaturePercentage || 0) +
                      parseFloat(newFormData.spottedPercentage || 0)
                    ).toFixed(2);
                    
                    // Recalculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(totalImpurities) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate quality rating based on TDR
                    const qualityData = calculateQualityRating(totalDefectiveRate);
                    
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      qualityRating: qualityData.qualityRating
                    };
                  });
                }}
              />
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Percentage: {formData.rottenPercentage || '0.00'}%
              </p>
            </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total Impurities %*
                </label>
                <input
                  id="totalImpuritiesPercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalImpuritiesPercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Immature (g)*
                </label>
                <InputField
                  id="immatureG"
                  type="number"
                  step="0.01"
                  placeholder="Enter immature in grams (Sample: 1000g)"
                  value={formData.immatureG || ''}
                  onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      immatureG: value
                    };
                    // Convert grams to percentage based on 1000g sample
                    const immatureGrams = parseFloat(value || 0);
                    const immaturePercentage = ((immatureGrams / 1000) * 100).toFixed(2);
                    newFormData.immaturePercentage = immaturePercentage;
                    
                    // Calculate total SP+IM percentage
                    const spottedPercentage = parseFloat(newFormData.spottedPercentage || 0);
                    const totalSpPlusIm = (
                      parseFloat(immaturePercentage) +
                      spottedPercentage
                    ).toFixed(2);
                    
                    // Calculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(newFormData.totalImpuritiesPercentage || 0) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate quality rating based on TDR
                    const qualityData = calculateQualityRating(totalDefectiveRate);
                    
                    // Calculate Outturn (LBS) using formula: (Good + (Spotted + Immature) / 2) * 0.176
                    const good = parseFloat(newFormData.goodMeasuredInGrams || 0);
                    const spottedGrams = parseFloat(newFormData.spottedG || 0);
                    const outturnLbs = (good + (spottedGrams + immatureGrams) / 2) * 0.176;
                    newFormData.outturn = outturnLbs > 0 ? outturnLbs.toFixed(2) : '0.00';
                    
                    // Also calculate expected kernel outturn if outturn exists
                    if (newFormData.outturn && newFormData.outturn !== '0.00') {
                      const step1 = parseFloat(newFormData.outturn) / 2.2046;
                      const step2 = step1 * 1.25;
                      newFormData.expectedKernelOutturn = ((step2 / 100) * 100).toFixed(2);
                    }
                    
                    return {
                      ...newFormData,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      qualityRating: qualityData.qualityRating
                    };
                  });
                }}
              />
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Percentage: {formData.immaturePercentage || '0.00'}%
              </p>
            </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Spotted (g)*
                </label>
                <InputField
                  id="spottedG"
                type="number"
                step="0.01"
                placeholder="Enter spotted in grams (Sample: 1000g)"
                value={formData.spottedG || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      spottedG: value
                    };
                    // Convert grams to percentage based on 1000g sample
                    const spottedGrams = parseFloat(value || 0);
                    const spottedPercentage = ((spottedGrams / 1000) * 100).toFixed(2);
                    newFormData.spottedPercentage = spottedPercentage;
                    
                    // Calculate total SP+IM percentage
                    const immaturePercentage = parseFloat(newFormData.immaturePercentage || 0);
                    const totalSpPlusIm = (
                      immaturePercentage +
                      parseFloat(spottedPercentage)
                    ).toFixed(2);
                    
                    // Calculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(newFormData.totalImpuritiesPercentage || 0) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate quality rating based on TDR
                    const qualityData = calculateQualityRating(totalDefectiveRate);
                    
                    // Calculate Outturn (LBS) using formula: (Good + (Spotted + Immature) / 2) * 0.176
                    const good = parseFloat(newFormData.goodMeasuredInGrams || 0);
                    const immatureGrams = parseFloat(newFormData.immatureG || 0);
                    const outturnLbs = (good + (spottedGrams + immatureGrams) / 2) * 0.176;
                    newFormData.outturn = outturnLbs > 0 ? outturnLbs.toFixed(2) : '0.00';
                    
                    // Also calculate expected kernel outturn if outturn exists
                    if (newFormData.outturn && newFormData.outturn !== '0.00') {
                      const step1 = parseFloat(newFormData.outturn) / 2.2046;
                      const step2 = step1 * 1.25;
                      newFormData.expectedKernelOutturn = ((step2 / 100) * 100).toFixed(2);
                    }
                    
                    return {
                      ...newFormData,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      qualityRating: qualityData.qualityRating
                    };
                  });
                }}
              />
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Percentage: {formData.spottedPercentage || '0.00'}%
              </p>
            </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total SP+IM %*
                </label>
                <input
                  id="totalSpPlusImPercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalSpPlusImPercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total Defective Rate %*
                </label>
                <input
                  id="totalDefectiveRatePercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalDefectiveRatePercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Quality Ratings */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Quality Ratings
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Quality Rating* (Auto-calculated)
                </label>
                {(() => {
                  const qualityData = calculateQualityRating(formData.totalDefectiveRatePercentage);
                  return (
                    <div className="mt-1">
                      {formData.totalDefectiveRatePercentage ? (
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm ${qualityData.ratingColor || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'}`}>
                          <span>{qualityData.qualityRating || 'Calculating...'}</span>
                          <span className="text-xs opacity-75">(TDR: {formData.totalDefectiveRatePercentage}%)</span>
                        </div>
                      ) : (
                        <div className="px-4 py-2 rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-900/30 dark:text-gray-400 text-sm">
                          Enter quality measurements to calculate rating
                        </div>
                      )}
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Rating Scale: 0-6.9% (Very Good) | 7-10.9% (Good) | 11-15.9% (Regular) | 16-20.9% (Bad) | 21%+ (Very Bad)
                      </p>
                    </div>
                  );
                })()}
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Expected Kernel Outturn %*
                </label>
                <input
                  id="expectedKernelOutturn"
                  type="number"
                  step="0.01"
                  value={formData.expectedKernelOutturn}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-brand-500 hover:bg-brand-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit Analysis"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddRawRCN;

